import { useFormik } from "formik";
import { Link, useNavigate, useLocation } from "react-router-dom";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { useDispatch } from "react-redux";
import { AppDispatch } from "redux/store";
import { signIn } from "../../redux/reducers/AuthSlice/AuthSlice";
import { getCurrentUser } from "../../redux/reducers/UserSlice/UserSlice";
import SocialAuth from "../../components/SocialAuth/SocialAuth";
import successHandler from "../../utils/helpers/SuccessHandler";
import { LoginSchema } from "../../utils/formSchema";
import FormField from "../../shared/FormField/FormField";
import { useEffect } from "react";
import { isAuthenticated } from "utils/helpers/CommonHelper";
import SEO from "components/SEO/SEO";

const PageLogin = ({ className = "" }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();

  const formik = useFormik({
    initialValues: { email: "", password: "" },
    validationSchema: LoginSchema,
    onSubmit: (values, { setSubmitting, resetForm }) => {
      setSubmitting(true);
      dispatch(signIn({ email: values.email, password: values.password }))
        .unwrap()
        .then((response) => {
          if (response?.success) {
            localStorage.setItem("authToken", response?.access_token);
            successHandler(response?.message || "Logged in successfully");
            dispatch(getCurrentUser())
              .unwrap()
              .then((res) => {
                if (res?.success) {
                  const redirectPath = location.state?.from || "/";
                  navigate(res?.data?.email_verified ? redirectPath : "/account");
                }
              });
            resetForm();
          }
        })
        .catch((error) => console.log("Error in login: ", error))
        .finally(() => setSubmitting(false));
    },
  });

  useEffect(() => {
    if (isAuthenticated()) navigate("/", { replace: true });
  }, []);

  return (
    <div className={`nc-PageLogin min-h-screen ${className}`} data-nc-id="PageLogin">
      <SEO
        title="Login"
        description="Log in to your Nomadroof account to manage your student accommodation bookings in Lima, Peru."
        canonical="/login"
        noIndex={true}
      />

      <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">

        {/* LEFT PANEL - hidden on mobile */}
        <div className="hidden lg:flex flex-col justify-between p-12 bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] relative overflow-hidden">
          {/* Glow effects */}
          <div className="absolute top-[-100px] right-[-100px] w-[500px] h-[500px] rounded-full bg-[radial-gradient(circle,rgba(245,105,72,0.15)_0%,transparent_70%)]" />
          <div className="absolute bottom-[50px] left-[-50px] w-[300px] h-[300px] rounded-full bg-[radial-gradient(circle,rgba(245,105,72,0.1)_0%,transparent_70%)]" />

          {/* Brand */}
          <div className="flex items-center gap-3 z-10">
            <div className="w-9 h-9 bg-[#F56948] rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 fill-white" viewBox="0 0 24 24">
                <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
                <polyline points="9 22 9 12 15 12 15 22"/>
              </svg>
            </div>
            <span className="text-white font-bold text-xl tracking-tight">Nomadroof</span>
          </div>

          {/* Headline */}
          <div className="z-10">
            <h1 className="text-5xl font-bold text-white leading-tight tracking-tight mb-4">
              Your home<br />in{" "}
              <span className="text-[#F56948]">Lima</span>
              <br />awaits.
            </h1>
            <p className="text-white/50 text-base leading-relaxed max-w-xs">
              Verified student accommodation for students doing their Erasmus. Find your perfect room in minutes.
            </p>
          </div>

          {/* Stats */}
          <div className="flex gap-10 z-10">
            {[
              { number: "15+", label: "Properties" },
              { number: "12", label: "Districts" },
              { number: "4.9★", label: "Rating" },
            ].map((stat) => (
              <div key={stat.label} className="flex flex-col gap-1">
                <span className="text-white font-bold text-2xl">{stat.number}</span>
                <span className="text-white/40 text-xs uppercase tracking-wider">{stat.label}</span>
              </div>
            ))}
          </div>

          {/* Floating card */}
          <div className="absolute bottom-36 right-[-20px] bg-white/10 backdrop-blur-xl border border-white/10 rounded-2xl px-4 py-3 flex items-center gap-3 z-20 animate-bounce">
            <div className="flex">
              {["M", "S", "A"].map((l, i) => (
                <div
                  key={l}
                  className={`w-7 h-7 rounded-full border-2 border-white/20 flex items-center justify-center text-white text-xs font-semibold ${i !== 0 ? "-ml-2" : ""} ${["bg-indigo-500", "bg-pink-500", "bg-teal-500"][i]}`}
                >
                  {l}
                </div>
              ))}
            </div>
            <div className="flex flex-col">
              <span className="text-white text-xs font-semibold">3 students joined today</span>
              <span className="text-white/40 text-xs">Lima, Miraflores district</span>
            </div>
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div className="flex items-center justify-center px-6 py-16 lg:px-16 bg-white dark:bg-neutral-900">
          <div className="w-full max-w-sm">

            {/* Header */}
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight mb-1">
                Welcome back
              </h2>
              <p className="text-sm text-neutral-400">
                Sign in to manage your accommodation
              </p>
            </div>

            {/* Google */}
            <SocialAuth />

            {/* Divider */}
            <div className="flex items-center gap-3 my-6">
              <div className="flex-1 h-px bg-neutral-200 dark:bg-neutral-700" />
              <span className="text-xs text-neutral-400 uppercase tracking-widest font-medium">
                or email
              </span>
              <div className="flex-1 h-px bg-neutral-200 dark:bg-neutral-700" />
            </div>

            {/* Form */}
            <form onSubmit={formik.handleSubmit} className="space-y-4">
              <FormField
                label="Email address"
                name="email"
                type="email"
                placeholder="you@example.com"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && formik.errors.email}
              />
              <FormField
                label="Password"
                placeholder="••••••••"
                name="password"
                type="password"
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.password && formik.errors.password}
              />

              <div className="flex justify-end">
                <Link
                  to="/edit_password"
                  className="text-xs text-[#F56948] hover:underline"
                >
                  Forgot password?
                </Link>
              </div>

              <ButtonPrimary type="submit" disabled={formik.isSubmitting} className="w-full">
                {formik.isSubmitting ? "Signing in..." : "Sign in"}
              </ButtonPrimary>
            </form>

            {/* Sign up */}
            <p className="text-center text-sm text-neutral-400 mt-6">
              Don't have an account?{" "}
              <Link to="/signup" className="text-[#F56948] font-medium hover:underline">
                Create one free
              </Link>
            </p>

          </div>
        </div>
      </div>
    </div>
  );
};

export default PageLogin;